import { useEffect, useRef, useState, useMemo } from "react"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { faPen, faXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, IconButton, Popover, Stack, Typography } from "@mui/material"
import { DeliveryTimesField, MeetingServiceConfig, DynamicConfigKey } from "../../form/formTypes"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { CreateOrderDialogFlow, CreateOrderFlowType, SelectedPriceListItem } from "../../../createOrderTypes"
import { getOpeningLimitsForDate, MAX_DELIVERIES, sortedDeliveryTimes, validateDeliveryDate } from "./helpers"
import { CreateUpdateDeliveryTimePopover } from "./CreateUpdateDeliveryTimePopover"
import { NullableCalendarEvent } from "../../resource/calendar/fullCalendarTypes"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { useConfirmAbandonDialog } from "../../../hooks/useConfirmAbandonDialog"
import { AddDeliveryTimeButton } from "./components/AddDeliveryTimeButton"
import { DeliveryTimeDisplay } from "./components/DeliveryTimeDisplay"
import { StandardButton } from "../../../../buttons/StandardButton"
import { ConfirmModal } from "../../../../dialogs/ConfirmModal"
import { defaultMultipleDeliveries } from "../../form/formLib"

const useStyles = makeStyles()((theme) => ({
    title: {
        color: theme.palette.tailwind.slate[500],
    },
    container: {
        display: "flex",
        alignItems: "center",
        background: theme.palette.tailwind.slate[100],
        padding: "0.5rem",
        borderRadius: "1.5rem",
        width: "fit-content",
        gap: "0.5rem",
        position: "relative",
    },
    iconButton: {
        padding: ".5rem",
        color: theme.palette.tailwind.slate[500],
        "& svg": {
            fontSize: "1.25rem",
        },
    },
    closeButton: {
        position: "absolute",
        top: ".5rem",
        right: ".5rem",
    },
    classNameOverride: {
        padding: "1.25rem 1.5rem 3.25rem",
    },
    disabledContent: {
        pointerEvents: "none",
    },
}))

type DeliveryTimeSelectorProps = Readonly<{
    meetingServiceConfiguration: MeetingServiceConfig
    handleCloseMainDialog: () => void
    flowConfig?: CreateOrderDialogFlow
    allowOutsideBusinessHours: boolean
    disabled: boolean
}>

export function DeliveryTimeSelector({
    meetingServiceConfiguration,
    handleCloseMainDialog,
    flowConfig,
    allowOutsideBusinessHours,
    disabled,
}: DeliveryTimeSelectorProps) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()

    const [{ value: priceList }] = useField<SelectedPriceListItem>("priceList")
    const [{ value: selectedTimesValue }, , selectedTimesHelpers] = useField<DeliveryTimesField | undefined>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )
    const selectedTimes: DeliveryTimesField = selectedTimesValue || defaultMultipleDeliveries

    const [{ value: localEvent }] = useField<NullableCalendarEvent>("event")

    const eventStartTime = useMemo(() => {
        return localEvent?.start ? dateTimeFactory.fromISO(localEvent.start) : null
    }, [localEvent?.start, dateTimeFactory])

    const isDisabledDatePicker = useMemo(() => {
        return !!localEvent?.resourceIds.length
    }, [localEvent?.resourceIds])

    const hasTimes = !!selectedTimes?.deliveryTimes?.length
    const isDeliverySet = Boolean(selectedTimes?.deliveryDate) && hasTimes

    const { deliveryTimes, closingDates } = meetingServiceConfiguration

    const effectiveClosingDates = useMemo(
        () => (allowOutsideBusinessHours ? [] : closingDates ?? []),
        [allowOutsideBusinessHours, closingDates],
    )
    const effectiveOpeningHours = useMemo(
        () => (allowOutsideBusinessHours ? [] : deliveryTimes ?? []),
        [allowOutsideBusinessHours, deliveryTimes],
    )

    const allowedWeekdays = useMemo(() => {
        return allowOutsideBusinessHours ? [1, 2, 3, 4, 5, 6, 7] : effectiveOpeningHours?.map((h) => h.dayOfWeek) || []
    }, [effectiveOpeningHours])

    const containerRef = useRef<HTMLDivElement>(null)
    const [popoverPosition, setPopoverPosition] = useState({ left: 0, top: 0 })
    const { confirmAbandonDialogState, handleOpenConfirmAbandonDialog, handleCloseConfirmAbandonDialog } =
        useConfirmAbandonDialog()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const open = Boolean(anchorEl)

    useEffect(() => {
        const shouldAutoOpen = !isDeliverySet || (flowConfig?.flow === CreateOrderFlowType.OUTLOOK && !isDeliverySet)

        if (shouldAutoOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect()
            setPopoverPosition({
                left: 40,
                top: rect.bottom,
            })
            setAnchorEl(containerRef.current)
        }
    }, [isDeliverySet, flowConfig])

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
        const rect = event.currentTarget.getBoundingClientRect()
        setPopoverPosition({
            left: rect.left,
            top: rect.bottom,
        })
        setAnchorEl(event.currentTarget)
    }

    const handlePopoverClose = () => {
        // Only allow closing if there are selected times
        const isValidDate =
            isDeliverySet &&
            validateDeliveryDate(
                dateTimeFactory.fromISO(selectedTimes.deliveryDate!),
                allowedWeekdays,
                effectiveClosingDates,
            ).isValidDeliveryDate

        if (!isValidDate) {
            handleOpenConfirmAbandonDialog()
            return
        }

        const deliveryDateTime = dateTimeFactory.fromISO(selectedTimes.deliveryDate!)
        const limits = getOpeningLimitsForDate(deliveryDateTime, effectiveOpeningHours)

        if (!eventStartTime || !limits) {
            setAnchorEl(null)
            return
        }

        const minBound = deliveryDateTime.set({ hour: limits.minTime.hour, minute: limits.minTime.minute })
        const maxBound = deliveryDateTime.set({ hour: limits.maxTime.hour, minute: limits.maxTime.minute })

        if (eventStartTime < minBound || eventStartTime > maxBound) {
            handleOpenConfirmAbandonDialog()
        } else {
            setAnchorEl(null)
        }
    }

    const onSubmit = (deliveries: DeliveryTimesField) => {
        const sortedTimes = sortedDeliveryTimes(deliveries.deliveryTimes)
        const updatedDeliveries = {
            ...deliveries,
            activeTime: sortedTimes[0]?.time || null,
        }
        selectedTimesHelpers.setValue(updatedDeliveries)
        // Close popover only if we have at least one delivery time
        if (updatedDeliveries.deliveryTimes.length > 0) {
            setAnchorEl(null)
        }
    }

    const handleConfirm = () => {
        handleCloseConfirmAbandonDialog()
    }
    return (
        <Stack gap=".5rem" className={cx({ [classes.disabledContent]: disabled })}>
            {!disabled && (
                <Stack flexDirection="row" marginLeft="1rem">
                    {hasTimes && (
                        <LocalizedStrict id="create-order-delivery-times-update-delivery-time">
                            <StandardButton
                                size="small"
                                variant="text"
                                fullWidth={false}
                                onClick={handlePopoverOpen}
                                updateDefaultClasses={classes.iconButton}
                                endIcon={<FontAwesomeIcon icon={faPen} />}
                            >
                                Update delivery time
                            </StandardButton>
                        </LocalizedStrict>
                    )}
                </Stack>
            )}

            <Box className={classes.container} ref={containerRef}>
                {isDeliverySet ? (
                    <>
                        <DeliveryTimeDisplay />
                        {selectedTimes.deliveryTimes.length < MAX_DELIVERIES && !disabled && (
                            <AddDeliveryTimeButton openingHours={effectiveOpeningHours} />
                        )}
                    </>
                ) : (
                    <LocalizedStrict id="create-order-delivery-times-add-delivery-time" attrs={{ title: true }}>
                        <Typography variant="body2" className={classes.title}>
                            Add delivery time:
                        </Typography>
                    </LocalizedStrict>
                )}
            </Box>

            {open && (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    anchorReference="anchorPosition"
                    anchorPosition={popoverPosition}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                    disableRestoreFocus
                >
                    <Box>
                        <IconButton onClick={handlePopoverClose} className={classes.closeButton}>
                            <FontAwesomeIcon icon={faXmark} />
                        </IconButton>

                        <CreateUpdateDeliveryTimePopover
                            initialValues={selectedTimes}
                            eventStartTime={eventStartTime}
                            isDisabledDatePicker={isDisabledDatePicker}
                            priceList={priceList}
                            onSubmit={onSubmit}
                            flowConfig={flowConfig}
                            allowedWeekdays={allowedWeekdays}
                            closingDates={effectiveClosingDates}
                            openingHours={effectiveOpeningHours}
                            handlePopoverClose={handlePopoverClose}
                        />
                    </Box>
                </Popover>
            )}
            {confirmAbandonDialogState && (
                <LocalizedStrict
                    id="create-order-close-confirm-dialog"
                    attrs={{ title: true, buttonLabelSubmit: true }}
                >
                    <ConfirmModal
                        open={confirmAbandonDialogState}
                        onClose={handleConfirm}
                        handleClick={handleCloseMainDialog}
                        title="Discard changes"
                        message={
                            <LocalizedStrict id="create-order-close-confirm-dialog-message">
                                <Typography variant="body1">
                                    If you click Discard, all entered information will be lost.
                                </Typography>
                            </LocalizedStrict>
                        }
                        buttonLabelSubmit="Discard"
                        classNameOverride={classes.classNameOverride}
                    />
                </LocalizedStrict>
            )}
        </Stack>
    )
}
